import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FormikErrors, FormikTouched } from 'formik';
import { IoMdMail } from 'react-icons/io';
import { Turnstile } from '@marsidev/react-turnstile';
import { Form, CheckboxProps } from 'semantic-ui-react';

import { isProduction } from '@/settings/environment';
import { CapyCaptcha, SetCapyValues } from '@/components/shared/CapyCaptcha';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';
import { Spinner } from '@/components/shared/Spinner';

import * as styles from './index.module.scss';

export type Values = {
  email: string;
  ignoreCapyCaptcha: boolean;
  ignoreTurnstile: boolean;
};

type Props = {
  errors: FormikErrors<Values>;
  handleChange: ((e: React.ChangeEvent<HTMLInputElement>) => void) &
    ((event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => void);
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isDisabled: boolean;
  touched: FormikTouched<Values>;
  values: Values;
  capyError: boolean;
  setCapyValues: SetCapyValues;
  // Turnstile 関連
  useTurnstile: boolean;
  turnstileError: string;
  turnstileLoading: boolean;
  onTurnstileSuccess: (token: string) => void;
  onTurnstileError: () => void;
  featureFlagLoading: boolean;
};
export const FormContents: React.FC<Props> = ({
  errors,
  handleChange,
  handleSubmit,
  isDisabled,
  touched,
  values,
  capyError,
  setCapyValues,
  useTurnstile,
  turnstileError,
  turnstileLoading,
  onTurnstileSuccess,
  onTurnstileError,
  featureFlagLoading,
}) => {
  const { t, i18n } = useTranslation();

  // Feature Flag読み込み中はSpinnerを表示
  if (featureFlagLoading) {
    return (
      <div className={styles.loaderContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="email">
          {t('ui.email.label')}
        </label>
        <div className={classnames(styles.inputWrapper, styles.iconLeft)}>
          <IoMdMail className={styles.icon} />
          <input
            id="email"
            data-test="email-field"
            className={classnames(styles.input, {
              [styles.error]: errors.email && touched.email,
            })}
            name="email"
            type="email"
            onChange={handleChange}
            placeholder={t('ui.email.placeholder')}
            value={values.email}
          />
        </div>
        {touched.email && errors.email && (
          <div>
            <Label>{errors.email}</Label>
          </div>
        )}
      </div>

      {/* Feature Flag による切り替え: Turnstile または Capy */}
      {useTurnstile ? (
        // Turnstileモード
        <div className={styles.turnstileWrapper}>
          <Turnstile
            siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ''}
            options={{
              language: i18n.language,
              theme: 'light',
            }}
            onSuccess={onTurnstileSuccess}
            onError={onTurnstileError}
          />
          {turnstileError && (
            <div className={styles.turnstileError} data-test="turnstile-error">
              <Label>{turnstileError}</Label>
            </div>
          )}
          {/* 開発環境のみスキップチェックボックスを表示 */}
          {!isProduction() && (
            <Form.Checkbox
              label={t('ui.turnstile.ignore')}
              id="ignoreTurnstile"
              name="ignoreTurnstile"
              data-test="ignore-turnstile"
              onChange={handleChange}
            />
          )}
        </div>
      ) : (
        // Capyモード
        <div className={styles.capyWrapper}>
          <CapyCaptcha onChange={setCapyValues} />
          <div>{t('ui.capyCaptcha.description')}</div>
          {capyError && <Label>{t('ui.capyCaptcha.error')}</Label>}
          {!isProduction() && (
            <label className={styles.checkbox} htmlFor="ignoreCapyCaptcha">
              <input
                type="checkbox"
                id="ignoreCapyCaptcha"
                name="ignoreCapyCaptcha"
                data-test="ignore-capy-captcha"
                onChange={handleChange}
                checked={values.ignoreCapyCaptcha}
              />
              <span className={styles.checkboxBox}></span>
              <span className={styles.checkboxLabel}>
                {t('ui.capyCaptcha.ignore')}
              </span>
            </label>
          )}
        </div>
      )}

      <div className="submit-button">
        <SharedButton
          dataTest="do-verify"
          disabled={
            useTurnstile
              ? // ignoreTurnstile=true の場合のみ turnstileLoading を無視して送信可能に
                (turnstileLoading && !values.ignoreTurnstile) || isDisabled
              : isDisabled
          }
          type="submit"
        >
          {t('ui.action.register')}
        </SharedButton>
      </div>
    </form>
  );
};
